import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import featureEnablementService, { FeatureToggle } from '../services/featureEnablementService';

export type FeatureAccessLevel = 'read_only' | 'standard' | 'advanced' | 'admin';

const LEVEL_ORDER: FeatureAccessLevel[] = ['read_only', 'standard', 'advanced', 'admin'];
const LEVEL_INDEX: Record<FeatureAccessLevel, number> = LEVEL_ORDER.reduce(
  (acc, level, index) => {
    acc[level] = index;
    return acc;
  },
  {} as Record<FeatureAccessLevel, number>
);

let myFeatureCache: FeatureToggle[] | null = null;
let cacheIdentityKey: string | null = null;
let cachePromise: Promise<void> | null = null;
let sharedPollInterval: ReturnType<typeof setInterval> | null = null;
let sharedPollSubscribers = 0;
let pollingIdentityKey: string | null = null;
let suspendedIdentityKey: string | null = null;
let authFailureIdentityKey: string | null = null;
// Set when /my-features/ fails with a NON-401 (5xx / network / timeout) error
// and bounded retries are exhausted. Without this the gate's isLoading would
// stay true forever (features null, no auth failure) -> infinite spinner.
let loadErrorIdentityKey: string | null = null;
let featureLoadRetryCount = 0;
const MAX_FEATURE_LOAD_RETRIES = 4;

const featureFlagListeners = new Set<() => void>();

const areFeaturesEquivalent = (
  current: FeatureToggle[] | null,
  next: FeatureToggle[] | null
): boolean => {
  if (!current || !next) {
    return false;
  }
  if (current.length !== next.length) {
    return false;
  }

  const nextByCode = new Map(next.map((feature) => [feature.code, feature]));
  return current.every((oldFeature) => {
    const newFeature = nextByCode.get(oldFeature.code);
    if (!newFeature) {
      return false;
    }

    return (
      oldFeature.enabled === newFeature.enabled &&
      oldFeature.org_permit === newFeature.org_permit &&
      oldFeature.domain_permit === newFeature.domain_permit &&
      oldFeature.user_permit === newFeature.user_permit &&
      oldFeature.has_access === newFeature.has_access &&
      oldFeature.access_level === newFeature.access_level
    );
  });
};

const loadFeatureCache = async (identityKey: string) => {
  try {
    // skipAuthFailure: a feature-flag fetch must NEVER be the request that forces
    // a global logout. On a transient 401 (access cookie momentarily expired, or
    // the origin briefly unreachable) this would otherwise call handleAuthFailure
    // and kick the user to /login. We fail silently here; real auth recovery is
    // driven by the user's own foreground requests + the coordinated token
    // refresh, and the 401 is still handled below (cache cleared, retried later).
    const response = await featureEnablementService.getMyFeatures({
      skipAuthFailure: true,
    });
    myFeatureCache = response.features;
    cacheIdentityKey = identityKey;
    suspendedIdentityKey = null;
    authFailureIdentityKey = null;
    loadErrorIdentityKey = null;
    featureLoadRetryCount = 0;
  } catch (error: any) {
    // Suppress 401 errors during initial load (expected if user not logged in or session expired)
    if (error?.response?.status === 401) {
      myFeatureCache = null;
      cacheIdentityKey = null;
      authFailureIdentityKey = identityKey;
      return;
    }
    throw error;
  }
};

export const invalidateFeatureFlags = (resetIdentity: boolean = false) => {
  myFeatureCache = null;
  cachePromise = null;
  authFailureIdentityKey = null;
  loadErrorIdentityKey = null;
  featureLoadRetryCount = 0;
  if (resetIdentity) {
    cacheIdentityKey = null;
    suspendedIdentityKey = null;
  }
  featureFlagListeners.forEach((listener) => listener());
};

const isFeatureEnabledInList = (
  features: FeatureToggle[],
  featureCode: string,
  requiredLevel: FeatureAccessLevel
) => {
  const feature = features.find((f) => f.code === featureCode);
  if (!feature) {
    return false;
  }
  if (feature.is_core_feature) {
    return true;
  }
  // New system: feature.enabled means it's enabled for the hospital
  // has_access (if present) means current user has access based on permit flags
  if (feature.has_access !== undefined) {
    return feature.has_access;
  }
  // Fallback to old system for backward compatibility
  if (!feature.enabled) {
    return false;
  }
  const featureLevel = (feature.access_level as FeatureAccessLevel) || 'standard';
  const featureLevelIndex = LEVEL_INDEX[featureLevel] ?? LEVEL_INDEX.standard;
  const requiredLevelIndex = LEVEL_INDEX[requiredLevel] ?? LEVEL_INDEX.standard;
  return featureLevelIndex >= requiredLevelIndex;
};

interface UseFeatureFlagOptions {
  enabled?: boolean;
}

export const useFeatureFlags = (options?: UseFeatureFlagOptions) => {
  const requestedFetch = options?.enabled ?? true;
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const validationState = useSelector((state: RootState) => state.auth.validationState);
  const userId = useSelector((state: RootState) => state.auth.user?.id ?? null);
  const hospitalId = useSelector((state: RootState) => state.auth.hospital?.id ?? null);
  const isSuperuser = useSelector((state: RootState) => state.auth.user?.is_superuser ?? false);

  const identityKey = userId ? `${userId}:${hospitalId ?? 'none'}` : null;
  const isSessionReady = validationState === 'valid';
  const shouldFetch = requestedFetch && isAuthenticated && isSessionReady;
  const hasAuthFailure = identityKey !== null && authFailureIdentityKey === identityKey;

  const [features, setFeatures] = useState<FeatureToggle[] | null>(() => {
    if (!identityKey) {
      return null;
    }
    return cacheIdentityKey === identityKey ? myFeatureCache : null;
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => {
      setFeatures(null);
      setVersion((prev) => prev + 1);
    };
    featureFlagListeners.add(listener);
    return () => {
      featureFlagListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (validationState !== 'valid' && identityKey && suspendedIdentityKey === identityKey) {
      suspendedIdentityKey = null;
    }
    if (validationState !== 'valid' && identityKey && authFailureIdentityKey === identityKey) {
      authFailureIdentityKey = null;
    }
    if (validationState !== 'valid' && identityKey && loadErrorIdentityKey === identityKey) {
      loadErrorIdentityKey = null;
      featureLoadRetryCount = 0;
    }
  }, [identityKey, validationState]);

  useEffect(() => {
    let cancelled = false;

    const ensureCache = async () => {
      if (!shouldFetch || !identityKey) {
        if (myFeatureCache !== null || cacheIdentityKey !== null || cachePromise) {
          invalidateFeatureFlags(true);
        }
        setFeatures(null);
        return;
      }

      // If we already know this identity is in auth-failure (the previous
      // /feature-enablement/my-features/ call returned 401), do NOT retry on
      // every invalidate / version bump. Wait until the identity actually
      // changes (= real re-authentication) before hitting the endpoint again.
      // Without this guard, calls to invalidateFeatureFlags() while the
      // server is still returning 401 cause repeated /my-features/ requests
      // — one of the symptoms of the production 401-storm loop.
      if (authFailureIdentityKey === identityKey) {
        if (!cancelled) {
          setFeatures(null);
        }
        return;
      }

      // A prior non-401 load failure exhausted its retries. Stop re-fetching
      // (the gate will render a load-error fallback instead of spinning) until
      // the identity changes or the user explicitly retries (invalidate).
      if (loadErrorIdentityKey === identityKey) {
        if (!cancelled) {
          setFeatures(null);
        }
        return;
      }

      // User/hospital identity changed: flush old cache before evaluating access
      if (cacheIdentityKey && cacheIdentityKey !== identityKey) {
        invalidateFeatureFlags(true);
      }

      if (!myFeatureCache || cacheIdentityKey !== identityKey) {
        if (!cachePromise) {
          cachePromise = loadFeatureCache(identityKey).finally(() => {
            cachePromise = null;
          });
        }
        try {
          await cachePromise;
        } catch (error: any) {
          if (error?.response?.status === 401) {
            if (!cancelled) {
              setFeatures(null);
              setVersion((v) => v + 1); // re-render so isLoading recomputes -> false
            }
            return;
          }
          // Non-401 (5xx / network / timeout — e.g. a brief origin blip). Do NOT
          // re-throw and strand the gate on an infinite spinner. Allow a re-fetch
          // and retry with backoff; after MAX_FEATURE_LOAD_RETRIES, set a load
          // error so isLoading resolves and the gate shows a retry fallback.
          cachePromise = null;
          if (featureLoadRetryCount < MAX_FEATURE_LOAD_RETRIES) {
            featureLoadRetryCount += 1;
            const delay = Math.min(1000 * 2 ** (featureLoadRetryCount - 1), 8000);
            setTimeout(() => {
              if (!cancelled) {
                setVersion((v) => v + 1); // re-run ensureCache (re-fetch)
              }
            }, delay);
            return;
          }
          loadErrorIdentityKey = identityKey;
          if (!cancelled) {
            setFeatures(null);
            setVersion((v) => v + 1); // re-render so isLoading recomputes -> false
          }
          return;
        }
      }

      // A 401 does NOT reject: loadFeatureCache swallows it and only sets the
      // module-level authFailureIdentityKey. That mutation is invisible to
      // React, so without an explicit re-render here the gate keeps rendering
      // the value isLoading had BEFORE the fetch (true) — forever. That is the
      // production "page suddenly went into loading and never came back" bug:
      // a dead session 401s /my-features/, the flag flips, nothing re-renders,
      // and the user stares at a spinner with no way out. Bump version so
      // isLoading recomputes to false and the gate shows its session fallback.
      // The re-run then exits early on the authFailureIdentityKey guard above,
      // so this settles after exactly one extra render (no loop).
      if (authFailureIdentityKey === identityKey) {
        if (!cancelled) {
          setFeatures(null);
          setVersion((v) => v + 1);
        }
        return;
      }

      if (!cancelled && cacheIdentityKey === identityKey) {
        setFeatures(myFeatureCache);
      }
    };

    ensureCache();

    return () => {
      cancelled = true;
    };
  }, [version, shouldFetch, identityKey]);

  // Shared polling for permission changes (single interval across all consumers)
  useEffect(() => {
    if (!shouldFetch || !identityKey) {
      return;
    }

    sharedPollSubscribers += 1;
    pollingIdentityKey = identityKey;

    if (!sharedPollInterval) {
      sharedPollInterval = setInterval(async () => {
        if (!pollingIdentityKey || cacheIdentityKey !== pollingIdentityKey) {
          return;
        }

        try {
          const freshFeatures = await featureEnablementService.getMyFeatures({
            skipAuthFailure: true,
          });

          if (cacheIdentityKey !== pollingIdentityKey) {
            return;
          }

          if (!areFeaturesEquivalent(myFeatureCache, freshFeatures.features)) {
            myFeatureCache = freshFeatures.features;
            suspendedIdentityKey = null;
            featureFlagListeners.forEach((listener) => listener());
          }
        } catch (error: any) {
          if (error?.response?.status === 401 && pollingIdentityKey) {
            myFeatureCache = null;
            cacheIdentityKey = null;
            suspendedIdentityKey = pollingIdentityKey;
            if (sharedPollInterval) {
              clearInterval(sharedPollInterval);
              sharedPollInterval = null;
            }
            sharedPollSubscribers = 0;
            pollingIdentityKey = null;
            featureFlagListeners.forEach((listener) => listener());
          }
          // Silently fail polling - don't spam console or trigger global logout
        }
      }, 30000);
    }

    return () => {
      sharedPollSubscribers = Math.max(0, sharedPollSubscribers - 1);
      if (sharedPollSubscribers === 0 && sharedPollInterval) {
        clearInterval(sharedPollInterval);
        sharedPollInterval = null;
        pollingIdentityKey = null;
      }
    };
  }, [shouldFetch, identityKey]);

  const hasFeature = useCallback(
    (featureCode: string, requiredLevel: FeatureAccessLevel = 'standard') => {
      // Superusers have unrestricted access to all features — no enablement required
      if (isSuperuser) {
        return true;
      }
      if (!features) {
        return false;
      }
      return isFeatureEnabledInList(features, featureCode, requiredLevel);
    },
    [isSuperuser, features]
  );

  const hasLoadError = identityKey !== null && loadErrorIdentityKey === identityKey;

  return {
    // Superusers are never in a loading state for feature checks — they bypass all gates.
    // A non-401 load error also ends the loading state (so the gate shows a retry
    // fallback instead of spinning forever).
    isLoading: !isSuperuser && shouldFetch && !features && !hasAuthFailure && !hasLoadError,
    hasFeature,
    features,
    hasAuthFailure: !isSuperuser && hasAuthFailure,
    hasLoadError: !isSuperuser && hasLoadError,
  };
};

export const useFeatureEnabled = (
  featureCode: string,
  requiredLevel: FeatureAccessLevel = 'standard',
  options?: UseFeatureFlagOptions
) => {
  const { isLoading, hasFeature, features } = useFeatureFlags(options);

  const enabled = useMemo(() => {
    if (isLoading || !features) {
      return null;
    }
    return hasFeature(featureCode, requiredLevel);
  }, [featureCode, requiredLevel, hasFeature, isLoading, features]);

  return enabled;
};
